import { Component, inject, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ReCaptcha2Component } from 'ngx-captcha';
import { throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { AppSettingsService } from 'src/app/services/app-settings.service';
import { HttpService } from 'src/app/services/http.service';
import { UserService } from 'src/app/services/shared/user.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { CustomValidators } from 'src/app/shared/validators/custom.validators';
import { AgentDetailsResponse } from '../model/agentDetailsResponse.model';

@Component({
  selector: 'terra-change-email',
  templateUrl: './change-email.component.html'
})
export class ChangeEmailComponent implements OnChanges {
  @ViewChild('recaptcha') recaptcha: ReCaptcha2Component;

  @Input()
  model: AgentDetailsResponse;

  isLoading = false;
  isSaved = false;

  private readonly http = inject(HttpService);
  private readonly userService = inject(UserService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly appSettingsService = inject(AppSettingsService);

  formGroup = new UntypedFormGroup({
    password: new UntypedFormControl('', [Validators.required, Validators.minLength(6)]),
    email: new UntypedFormControl('', [Validators.required, CustomValidators.EmailWithSpaces]),

    recaptcha: new UntypedFormControl('', [Validators.required]),
  });


  hasError = (control, error) => this.formGroup.get(control).hasError(error);

  get isDisabled() {
    return this.model && this.model.ssoOnly;
  }

  get recaptchaPublicKey() {
   return this.appSettingsService.recaptchaPublicKey;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.model && changes.model.isFirstChange()) {
      if (this.model.ssoOnly) {
        this.formGroup.disable();
      }
    }
  }

  removeReadonly(event: FocusEvent) {
    const element = event.target as HTMLInputElement;
    element.removeAttribute('readonly');
  }

  changeEmail() {
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
    this.isSaved = false;

    if (this.isLoading) {
      return;
    }

    if (this.formGroup.invalid) {
      for (const i in this.formGroup.controls) {
        if (this.formGroup.controls[i]) {
          this.formGroup.controls[i].markAsTouched();
        }
      }
      return;
    }
    this.isLoading = true;

    this.userService
      .changeEmail(
        this.formGroup.get('email').value,
        this.formGroup.get('password').value,
        this.formGroup.get('recaptcha').value
      )
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.recaptcha.reloadCaptcha();
          this.formGroup.get('recaptcha').reset();
        }),
        tap(() => {
          this.isSaved = true;
          this.snackbarService.showGeneralMessage('Email updated');
          this.formGroup.reset();
        }),
        catchError(error => {
          if (error.error && error.error.message === 'The password is invalid') {
            this.formGroup.get('password').setErrors({ invalid: true });
          } else if (error.error && error.error.message === "3") { // The recaptcha is invalid
            this.formGroup.get('recaptcha').setErrors({ invalid: true });
          } else if (error.error && error.error.message === 'Email already exist!') {
            this.formGroup.get('email').setErrors({ emailexist: true });
          } else {
            this.formGroup.get('email').setErrors({ badrequest: true });
          }
           return throwError(() => error); 
        })
      )
      .subscribe();
  }
}
